import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calculator, Mail, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ForgotPassword() {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/auth/forgot-password`, null, {
        params: { email }
      });
      
      setEmailSent(true);
      toast.success(t('auth.resetLinkSent'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('auth.resetFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-blue-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Language Switcher */}
        <div className="flex justify-end mb-4">
          <Select value={i18n.language} onValueChange={changeLanguage}>
            <SelectTrigger className="w-24 h-9">
              <Mail className="h-4 w-4 mr-1" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="de">DE</SelectItem>
              <SelectItem value="en">EN</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center space-x-2 mb-4">
            <Calculator className="h-12 w-12 text-emerald-600" />
            <span className="text-3xl font-bold text-gray-900">ivoliq</span>
          </div>
          <p className="text-gray-600">{t('auth.forgotPasswordTitle')}</p>
        </div>

        {/* Form */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          {!emailSent ? (
            <>
              <h2 className="text-2xl font-bold text-center mb-2">{t('auth.forgotPassword')}</h2>
              <p className="text-gray-600 text-center mb-6">{t('auth.forgotPasswordDescription')}</p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('auth.email')}
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      placeholder={t('auth.emailPlaceholder')}
                      required
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-emerald-600 text-white py-2 px-4 rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? t('auth.pleaseWait') : t('auth.sendResetLink')}
                </button>
              </form>
            </>
          ) : (
            <div className="text-center">
              <div className="mb-4 p-4 bg-emerald-50 rounded-lg">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-100 rounded-full mb-4">
                  <Mail className="h-8 w-8 text-emerald-600" />
                </div>
                <h3 className="text-xl font-semibold text-emerald-800 mb-2">{t('auth.emailSentTitle')}</h3>
                <p className="text-gray-600 mb-4">{t('auth.emailSentDescription')}</p>
                <p className="text-sm text-gray-500">
                  {t('auth.checkSpam')}
                </p>
              </div>
            </div>
          )}

          <div className="mt-6 text-center">
            <Link to="/login" className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 font-medium">
              <ArrowLeft className="h-4 w-4" />
              {t('auth.backToLogin')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ForgotPassword;